import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IMG, imgProps } from "../data/assets";

const SHEETS = [
  { n: "01", t: "INSPECTION", img: "evInspection", d: "Robotic footage establishes condition before scope." },
  { n: "02", t: "BEFORE", img: "evBefore", d: "Photographic record at every access point." },
  { n: "03", t: "EXECUTION", img: "evExecution", d: "Zone-wise work against the agreed plan." },
  { n: "04", t: "AFTER", img: "evAfter", d: "The same positions, re-photographed clean." },
  { n: "05", t: "CHECKLIST", img: "evChecklist", d: "Signed against scope, line by line." },
  { n: "06", t: "CERTIFICATE", cert: true, d: "A service certificate for the audit file." },
  { n: "07", t: "HANDOVER", img: "evHandover", d: "Everything compiled. Signed off. Filed." },
];

// SCENE 06: the evidence pack — static sheet grid on every screen.
// (The pinned horizontal-scroll variant was retired: it read as broken on
// tall/short viewports. The grid keeps every sheet visible and crawlable.)
export function EvidenceScene() {
  return (
    <section id="evidence" className="noise-dark relative overflow-hidden py-16 text-zinc-100 md:py-24" aria-label="The DCC Evidence Standard">
      <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
      <div className="container-x relative">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">04 / The DCC Evidence Standard</p>
        <h2 className="h-display mt-6 max-w-5xl text-4xl text-zinc-50 sm:text-5xl lg:text-6xl">
          Cleaning is invisible <span className="text-dcc">until you prove it.</span>
        </h2>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="evidence-grid">
          {SHEETS.map((s) => (
            <EvidenceSheet key={s.n} s={s} />
          ))}
        </div>
        <Link to="/our-work" data-testid="evidence-work-link" className="btn-primary mt-14">
          View Our Work <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function EvidenceSheet({ s }) {
  return (
    <div className="flex flex-col border border-zinc-200 bg-white p-5 text-charcoal" data-testid={`evidence-sheet-${s.n}`}>
      <div className="flex items-start justify-between">
        <span className="tech-no">{s.n}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">DCC Evidence Standard</span>
      </div>
      {s.img && (
        <img src={IMG[s.img].src} alt={IMG[s.img].alt} {...imgProps(IMG[s.img].src)} loading="lazy" decoding="async" className="my-4 aspect-[16/10] w-full border border-zinc-100 object-cover" />
      )}
      {s.cert && (
        <img
          src="/assets/dcc/dcc-service-certificate.webp"
          alt="DCC Certificate of Cleanliness — the service certificate issued per project after documented completion"
          width="2000" height="1333"
          {...imgProps("/assets/dcc/dcc-service-certificate.webp", "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw")}
          loading="lazy" decoding="async"
          className="my-4 aspect-[16/10] w-full border border-zinc-100 object-contain"
          data-testid="evidence-certificate-img"
        />
      )}
      <div className="mt-auto">
        <p className="font-display text-xl font-semibold uppercase tracking-tight">{s.t}</p>
        <p className="mt-2 text-xs leading-relaxed text-zinc-500">{s.d}</p>
      </div>
    </div>
  );
}
